import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import useIsMobile from '../../utils/useIsMobile';

const TYPES = ['Implementation', 'POC'];

export default function ProjectTemplates() {
  const isMobile = useIsMobile();
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null); // full template with milestones
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ product_id: '', type: 'Implementation', name: '', description: '' });
  const [copyModal, setCopyModal] = useState(null); // { src } — open duplicate dialog
  const [copyForm, setCopyForm] = useState({ name: '', type: 'Implementation', product_id: '' });
  const [copying, setCopying] = useState(false);
  const [filterProduct, setFilterProduct] = useState('');
  const [filterType, setFilterType] = useState('');
  const [msg, setMsg] = useState(null);

  function notify(type, text) { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tmpl, prod] = await Promise.all([
        apiFetch('/project-templates'),
        apiFetch('/products'),
      ]);
      setTemplates(tmpl);
      setProducts(prod.products || prod);
    } catch (e) { notify('error', e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function selectTemplate(t) {
    setSelected(t);
    try {
      const full = await apiFetch(`/project-templates/${t.id}`);
      setEditing(JSON.parse(JSON.stringify(full))); // deep clone for editing
    } catch (e) { notify('error', e.message); }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await apiFetch('/project-templates', { method: 'POST', body: newForm });
      setShowNew(false);
      setNewForm({ product_id: '', type: 'Implementation', name: '', description: '' });
      notify('success', 'Template created');
      await load();
    } catch (e) { notify('error', e.message); }
  }

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    try {
      const result = await apiFetch(`/project-templates/${editing.id}`, {
        method: 'PUT',
        body: { name: editing.name, description: editing.description, type: editing.type, product_id: editing.product_id || null, milestones: editing.milestones },
      });
      notify('success', 'Template saved');
      setEditing(JSON.parse(JSON.stringify(result)));
      setTemplates(ts => ts.map(t => t.id === result.id ? { ...t, ...result } : t));
    } catch (e) { notify('error', e.message); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this template?')) return;
    try {
      await apiFetch(`/project-templates/${id}`, { method: 'DELETE' });
      notify('success', 'Deleted');
      setSelected(null); setEditing(null);
      await load();
    } catch (e) { notify('error', e.message); }
  }

  function openCopyModal(t) {
    setCopyForm({
      name: `Copy of ${t.name}`,
      type: t.type,                    // keep same type by default (change if needed)
      product_id: t.product_id || '',  // keep same product by default
    });
    setCopyModal(t);
  }

  async function handleCopy(e) {
    e.preventDefault();
    if (!copyModal) return;
    setCopying(true);
    try {
      // Single atomic copy — mints a new template id with the chosen name/type/product,
      // preserving all milestones+tasks from the source.
      const copy = await apiFetch(`/project-templates/${copyModal.id}/copy`, {
        method: 'POST',
        body: { name: copyForm.name, type: copyForm.type, product_id: copyForm.product_id || null },
      });
      notify('success', `"${copyForm.name}" created — editor opened`);
      setCopyModal(null);
      await load();
      // Open the new copy in the editor
      const full = await apiFetch(`/project-templates/${copy.id}`);
      setSelected(full);
      setEditing(JSON.parse(JSON.stringify(full)));
    } catch (e) { notify('error', e.message); }
    setCopying(false);
  }

  // ── Milestone / task helpers ───────────────────────────────────────────────

  function addMilestone() {
    setEditing(e => ({ ...e, milestones: [...(e.milestones || []), { id: `_new_${Date.now()}`, title: '', default_days: 0, tasks: [] }] }));
  }

  function updateMilestone(idx, field, value) {
    setEditing(e => {
      const ms = [...e.milestones];
      ms[idx] = { ...ms[idx], [field]: value };
      return { ...e, milestones: ms };
    });
  }

  function removeMilestone(idx) {
    setEditing(e => ({ ...e, milestones: e.milestones.filter((_, i) => i !== idx) }));
  }

  function addTask(mIdx) {
    setEditing(e => {
      const ms = [...e.milestones];
      ms[mIdx] = { ...ms[mIdx], tasks: [...(ms[mIdx].tasks || []), { id: `_new_${Date.now()}`, title: '' }] };
      return { ...e, milestones: ms };
    });
  }

  function updateTask(mIdx, tIdx, value) {
    setEditing(e => {
      const ms = [...e.milestones];
      const tasks = [...ms[mIdx].tasks];
      tasks[tIdx] = { ...tasks[tIdx], title: value };
      ms[mIdx] = { ...ms[mIdx], tasks };
      return { ...e, milestones: ms };
    });
  }

  function removeTask(mIdx, tIdx) {
    setEditing(e => {
      const ms = [...e.milestones];
      ms[mIdx] = { ...ms[mIdx], tasks: ms[mIdx].tasks.filter((_, i) => i !== tIdx) };
      return { ...e, milestones: ms };
    });
  }

  const card = { background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--border-subtle)', padding: '16px 20px' };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Project Templates</div>
          <div className="page-sub">Define standard milestones & tasks per product and engagement type</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Template</button>
      </div>

      {msg && (
        <div style={{ marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
          border: `1px solid ${msg.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
          color: msg.type === 'success' ? '#065f46' : '#dc2626' }}>
          {msg.text}
        </div>
      )}

      {/* New template modal */}
      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setShowNew(false)}>
          <div style={{ ...card, width: 480, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 20 }}>New Template</h3>
            <form onSubmit={handleCreate}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Product</label>
              <select className="input" style={{ marginBottom: 14 }}
                value={newForm.product_id} onChange={e => setNewForm(f => ({ ...f, product_id: e.target.value }))}>
                <option value="">— All products —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Type</label>
              <select className="input" style={{ marginBottom: 14 }}
                value={newForm.type} onChange={e => setNewForm(f => ({ ...f, type: e.target.value }))}>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Template Name *</label>
              <input className="input" required style={{ marginBottom: 14 }} placeholder="e.g. Standard Implementation"
                value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />

              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Description</label>
              <textarea className="input" rows={2} style={{ marginBottom: 20, resize: 'none' }}
                value={newForm.description} onChange={e => setNewForm(f => ({ ...f, description: e.target.value }))} />

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Template</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate modal */}
      {copyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setCopyModal(null)}>
          <div style={{ ...card, width: 460, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.2)' }}
               onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>Duplicate Template</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Copying <strong>{copyModal.name}</strong> — set the name, type and product for the new copy.
            </p>
            <form onSubmit={handleCopy}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>New Template Name *</label>
              <input className="input" required style={{ marginBottom: 14 }}
                value={copyForm.name} onChange={e => setCopyForm(f => ({ ...f, name: e.target.value }))} />

              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Type *</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {TYPES.map(t => (
                      <button key={t} type="button" onClick={() => setCopyForm(f => ({ ...f, type: t }))}
                        style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid',
                                 borderColor: copyForm.type === t ? '#0B6623' : 'var(--n-300)',
                                 background: copyForm.type === t ? '#f0fdf4' : 'var(--bg-surface)',
                                 color: copyForm.type === t ? '#0B6623' : 'var(--text-secondary)',
                                 fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Product</label>
                  <select className="input"
                    value={copyForm.product_id} onChange={e => setCopyForm(f => ({ ...f, product_id: e.target.value }))}>
                    <option value="">— All Products —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setCopyModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={copying}>
                  {copying ? 'Duplicating…' : '⎘ Create Copy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main two-column layout */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>

        {/* Left: filters + template list */}
        <div style={{ width: isMobile ? '100%' : 280, flexShrink: 0 }}>
          {/* Filters */}
          <div style={{ ...card, padding: 14, marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>PRODUCT</label>
            <select className="input" style={{ fontSize: 13, marginBottom: 10 }}
              value={filterProduct} onChange={e => setFilterProduct(e.target.value)}>
              <option value="">All Products</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>TYPE</label>
            <select className="input" style={{ fontSize: 13 }}
              value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {(filterProduct || filterType) && (
              <button onClick={() => { setFilterProduct(''); setFilterType(''); }}
                style={{ marginTop: 10, width: '100%', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
                         background: 'var(--n-100)', border: 'none', borderRadius: 6, padding: '6px 0', cursor: 'pointer' }}>
                Clear filters
              </button>
            )}
          </div>

          {(() => {
            const filtered = templates.filter(t =>
              (!filterProduct || String(t.product_id) === String(filterProduct)) &&
              (!filterType || t.type === filterType)
            );
            return loading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>Loading…</div>
            ) : templates.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                No templates yet. Create one to get started.
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: 24, color: 'var(--text-secondary)', fontSize: 13 }}>
                No templates match this filter.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>
                  {filtered.length} template{filtered.length !== 1 ? 's' : ''}
                </div>
                {filtered.map(t => (
                <div key={t.id}
                  style={{
                    ...card,
                    borderColor: selected?.id === t.id ? '#0B6623' : 'var(--border-subtle)',
                    background: selected?.id === t.id ? '#f0fdf4' : 'var(--bg-surface)',
                    transition: 'all .15s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', flex: 1 }}>{t.name}</div>
                    {t.is_master ? (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7',
                                     padding: '1px 7px', borderRadius: 99, flexShrink: 0 }}>🔒 MASTER</span>
                    ) : t.owner_id ? (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#ede9fe',
                                     padding: '1px 7px', borderRadius: 99, flexShrink: 0 }}>PERSONAL</span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {t.product_name || 'All Products'} · {t.type}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {t.milestone_count} milestones · {t.task_count} tasks
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <button onClick={() => selectTemplate(t)}
                      style={{ flex: 1, fontSize: 11, fontWeight: 700,
                               color: t.is_master ? 'var(--text-secondary)' : '#fff',
                               background: t.is_master ? 'var(--n-100)' : '#0B6623',
                               border: `1px solid ${t.is_master ? 'var(--n-300)' : '#0B6623'}`, borderRadius: 6,
                               padding: '4px 0', cursor: 'pointer' }}>
                      {t.is_master ? '👁 View' : '✏ Edit'}
                    </button>
                    <button onClick={() => openCopyModal(t)}
                      style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#0B6623',
                               background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
                               padding: '4px 0', cursor: 'pointer' }}>
                      ⎘ Duplicate
                    </button>
                  </div>
                </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Right: editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!editing ? (
            <div style={{ ...card, textAlign: 'center', padding: 48, color: 'var(--text-secondary)' }}>
              Select a template to edit its milestones and tasks
            </div>
          ) : (
            <div style={card}>
              {/* Master lock banner */}
              {editing.is_master && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 16px',
                              background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10 }}>
                  <span style={{ fontSize: 20 }}>🔒</span>
                  <div style={{ flex: 1, fontSize: 13, color: '#92400e' }}>
                    <b>Master template — read-only.</b> Master templates can't be edited or deleted. Duplicate it to create your own editable copy.
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => openCopyModal(editing)}>⎘ Duplicate</button>
                </div>
              )}
              {/* Template header */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                  <input value={editing.name} readOnly={editing.is_master} onChange={e => setEditing(ed => ({ ...ed, name: e.target.value }))}
                    style={{ fontSize: 18, fontWeight: 700, border: 'none', borderBottom: '2px solid var(--border-subtle)',
                             outline: 'none', background: 'transparent', color: 'var(--text-primary)', flex: 1, padding: '2px 0' }} />
                  {!editing.is_master && (
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(editing.id)}>Delete</button>
                      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Template'}
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, pointerEvents: editing.is_master ? 'none' : 'auto', opacity: editing.is_master ? 0.7 : 1 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>PRODUCT</label>
                    <select className="input" style={{ fontSize: 13 }}
                      value={editing.product_id || ''} onChange={e => setEditing(ed => ({ ...ed, product_id: e.target.value || null }))}>
                      <option value="">— All Products —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>TYPE</label>
                    <select className="input" style={{ fontSize: 13 }}
                      value={editing.type || 'Implementation'} onChange={e => setEditing(ed => ({ ...ed, type: e.target.value }))}>
                      {TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Milestones */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12,
                            pointerEvents: editing.is_master ? 'none' : 'auto', opacity: editing.is_master ? 0.75 : 1 }}>
                {(editing.milestones || []).map((m, mIdx) => (
                  <div key={m.id || mIdx} style={{ border: '1.5px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Milestone header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                                  background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#0B6623', background: '#f0fdf4',
                                     padding: '2px 8px', borderRadius: 99 }}>M{mIdx + 1}</span>
                      <input value={m.title} onChange={e => updateMilestone(mIdx, 'title', e.target.value)}
                        placeholder="Milestone title…"
                        style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent',
                                 fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Duration (days)</span>
                        <input type="number" min={0} value={m.default_days}
                          onChange={e => updateMilestone(mIdx, 'default_days', parseInt(e.target.value) || 0)}
                          style={{ width: 56, padding: '4px 8px', borderRadius: 6,
                                   border: '1.5px solid var(--border-subtle)', fontSize: 13, textAlign: 'center',
                                   background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
                        <button onClick={() => removeMilestone(mIdx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                                   color: '#dc2626', lineHeight: 1, padding: '2px 4px' }}>×</button>
                      </div>
                    </div>

                    {/* Tasks */}
                    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(m.tasks || []).map((t, tIdx) => (
                        <div key={t.id || tIdx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#94a3b8', fontSize: 14 }}>○</span>
                          <input value={t.title} onChange={e => updateTask(mIdx, tIdx, e.target.value)}
                            placeholder="Task title…"
                            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent',
                                     fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)',
                                     paddingBottom: 2 }} />
                          <button onClick={() => removeTask(mIdx, tIdx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14,
                                     color: '#dc2626', padding: '0 4px', lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                      <button onClick={() => addTask(mIdx)}
                        style={{ alignSelf: 'flex-start', fontSize: 12, color: '#0B6623', background: 'none',
                                 border: 'none', cursor: 'pointer', padding: '4px 0', fontWeight: 600, marginTop: 4 }}>
                        + Add task
                      </button>
                    </div>
                  </div>
                ))}

                <button onClick={addMilestone}
                  style={{ padding: '10px 0', borderRadius: 10, border: '1.5px dashed var(--border-subtle)',
                           background: 'transparent', color: 'var(--text-secondary)', fontSize: 13,
                           fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center',
                           justifyContent: 'center', gap: 6 }}>
                  + Add Milestone
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
